/**
 * useNotifications -- in-app notification store backed by the REST API.
 * Polls /api/notifications every 30 seconds for fresh data.
 *
 * For high/urgent notifications:
 *  - Plays a subtle beep via AudioContext (no audio file required)
 *  - Shows a native browser Notification (after requesting permission once)
 */
import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from '@/lib/api'

// ─────────────────────────────────────────────────────────────────────────────
// Types — map backend schema to frontend
// ─────────────────────────────────────────────────────────────────────────────

export type NotifType = 'recommendation' | 'trade' | 'alert' | 'risk' | 'system'
export type NotifPriority = 'low' | 'medium' | 'high' | 'urgent'

/** Kept for backward compat with NotificationDrawer severity config */
export type NotifSeverity = 'info' | 'success' | 'warning' | 'critical'

export interface Notification {
  id: string
  title: string
  message: string
  type: NotifType
  priority: NotifPriority
  severity: NotifSeverity  // derived from priority for icon mapping
  timestamp: Date
  read: boolean
  source: string           // derived from type for badge mapping
  data: Record<string, unknown>
}

// Map backend priority -> severity for icons
function priorityToSeverity(priority: NotifPriority): NotifSeverity {
  switch (priority) {
    case 'urgent': return 'critical'
    case 'high':   return 'warning'
    case 'medium': return 'info'
    case 'low':    return 'success'
  }
}

// Map backend type -> source label for badge
function typeToSource(type: NotifType, data?: Record<string, unknown>): string {
  if (type === 'alert' && data?.alert_type) return 'SmartAlert'
  switch (type) {
    case 'recommendation': return 'ARIA'
    case 'trade':          return 'Portfolio'
    case 'risk':           return 'Risk'
    case 'alert':          return 'Market'
    case 'system':         return 'Market'
  }
}

// Convert a raw API notification to our frontend shape
function fromApi(raw: Record<string, unknown>): Notification {
  const type = (raw.type as NotifType) || 'system'
  const priority = (raw.priority as NotifPriority) || 'medium'
  const data = (raw.data as Record<string, unknown>) || {}
  // Smart alerts embed their own severity in data — use it if present
  const alertSeverity = data.severity as string | undefined
  let severity: NotifSeverity
  if (alertSeverity === 'critical') severity = 'critical'
  else if (alertSeverity === 'warning') severity = 'warning'
  else if (alertSeverity === 'info' && type === 'alert') severity = 'info'
  else severity = priorityToSeverity(priority)
  return {
    id: String(raw.id),
    title: (raw.title as string) || '',
    message: (raw.message as string) || '',
    type,
    priority,
    severity,
    timestamp: new Date(raw.created_at as string),
    read: Boolean(raw.read),
    source: typeToSource(type, data),
    data,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Browser notification helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Plays a short 800 Hz beep using the Web Audio API — no audio file needed. */
function playNotificationSound(): void {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 800
    gain.gain.value = 0.1
    osc.start()
    osc.stop(ctx.currentTime + 0.15)
    // Close the context after the sound finishes to free resources
    osc.onended = () => ctx.close()
  } catch {
    // Ignore — may fail if AudioContext is blocked or not supported
  }
}

/** Requests Notification permission once and stores the result. */
async function requestNotificationPermission(): Promise<void> {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    Notification.permission !== 'default'
  ) {
    return
  }
  await Notification.requestPermission()
}

/** Shows a native browser push notification if permission is granted. */
function showBrowserNotification(title: string, body: string): void {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  ) {
    return
  }
  try {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'financegenie-alert',  // collapses duplicates
    })
  } catch {
    // Ignore — can fail in some browser contexts
  }
}

/** Returns true for priorities that warrant sound + browser push. */
function isHighPriority(priority: NotifPriority): boolean {
  return priority === 'high' || priority === 'urgent'
}

// ─────────────────────────────────────────────────────────────────────────────
// Zustand store
// ─────────────────────────────────────────────────────────────────────────────

interface NotifState {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  // Actions
  fetchAll: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markRead: (id: string) => void
  markAllRead: () => void
  dismiss: (id: string) => void
  clear: () => void
}

export const useNotifications = create<NotifState>()((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,

  fetchAll: async () => {
    try {
      const raw = await getNotifications(50)
      const mapped = (raw as Record<string, unknown>[]).map(fromApi)
      const unread = mapped.filter(n => !n.read).length

      // Detect newly-arrived high/urgent notifications (not yet in the store)
      const prev = useNotifications.getState().notifications
      const prevIds = new Set(prev.map(n => n.id))
      const fresh = mapped.filter(n => !prevIds.has(n.id) && !n.read && isHighPriority(n.priority))

      if (fresh.length > 0) {
        playNotificationSound()
        // Show one browser notification for the most recent fresh item
        const top = fresh[0]
        showBrowserNotification(top.title, top.message)
      }

      set({ notifications: mapped, unreadCount: unread, loading: false })
    } catch {
      // silently ignore — keeps stale data
    }
  },

  fetchUnreadCount: async () => {
    try {
      const { count } = await getUnreadCount()
      set({ unreadCount: count })
    } catch {
      // ignore
    }
  },

  markRead: async (id: string) => {
    // Optimistic update
    set(s => ({
      notifications: s.notifications.map(n =>
        n.id === id ? { ...n, read: true } : n,
      ),
      unreadCount: Math.max(0, s.unreadCount - (s.notifications.find(n => n.id === id && !n.read) ? 1 : 0)),
    }))
    try {
      await markNotificationRead(Number(id))
    } catch {
      // revert on error by refetching
      get().fetchAll()
    }
  },

  markAllRead: async () => {
    set(s => ({
      notifications: s.notifications.map(n => ({ ...n, read: true })),
      unreadCount: 0,
    }))
    try {
      await markAllNotificationsRead()
    } catch {
      get().fetchAll()
    }
  },

  dismiss: async (id: string) => {
    const was = get().notifications.find(n => n.id === id)
    set(s => ({
      notifications: s.notifications.filter(n => n.id !== id),
      unreadCount: was && !was.read ? Math.max(0, s.unreadCount - 1) : s.unreadCount,
    }))
    try {
      await deleteNotification(Number(id))
    } catch {
      get().fetchAll()
    }
  },

  clear: () => set({ notifications: [], unreadCount: 0 }),
}))


// ─────────────────────────────────────────────────────────────────────────────
// Polling hook — call once in Shell to start 30-second polling
// ─────────────────────────────────────────────────────────────────────────────

export function useNotificationPolling() {
  const fetchAll = useNotifications(s => s.fetchAll)
  const fetchUnreadCount = useNotifications(s => s.fetchUnreadCount)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    // Ask for browser Notification permission once on first visit
    requestNotificationPermission()

    // Initial fetch
    fetchAll()

    // Poll every 30 seconds
    const interval = setInterval(() => {
      fetchUnreadCount()
      fetchAll()
    }, 30_000)

    return () => {
      clearInterval(interval)
      started.current = false
    }
  }, [fetchAll, fetchUnreadCount])
}


// ─────────────────────────────────────────────────────────────────────────────
// Legacy helper — kept for backward compat but now creates notifications
// via the API (notifications are created server-side during analysis)
// ─────────────────────────────────────────────────────────────────────────────

export function pushAnalysisNotifications(_analysis: {
  risk: { overall_risk_score: number; position_warnings: Array<{ message: string }>; risk_label: string }
  optimization: { action: string; recommendation: string }
  behavioral: { stress_level: number }
  goals: { overall_on_track: boolean; summary: string }
}) {
  // Notifications are now created server-side in recommendations.py.
  // Just refetch to pick them up.
  useNotifications.getState().fetchAll()
}
